import React, {useState, useEffect} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {getPromoterService} from "../../../../services/getPromoterService";
import {getOrder, getOrderFromPendingPaymentId, postCreateTransaction} from "../../../../services/orderService";
import {getPaymentProviders} from "../../../../services/paymentProvidersService";
import Loader from "../../../../components/Loader/Loader";
import Header from "../Header/Header";
import Footer from "../../../../components/Footer";
import OrderDetails from "../order/OrderDetails";
import Breadcrumbs from "../../../../components/Checkout/Breadcrumbs/BreadCrumbs";
import MercadoPago from "./MercadoPago";
import {Helmet, HelmetProvider} from "react-helmet-async";
import Transferencia from "./Transferencia";
import Bank from "./Bank";
import Cash from "./Cash";
import Rebill from "./Rebill";
import useDataTravel from '../../../../hooks/useDataTravel';
import MetaTags from "../../../../components/MetaTags";
import { PAYMENT_PROVIDERS } from "../../../../constants/paymentProviders";
import whatsapp_icon from "../../../../assets/icons/whatsapp_green.svg";

const Payments = () => {
    const currentStep = 2;

    const [promoter, setPromoter] = useState(null);
    const [loading, setLoading] = useState(true);
    const [order, setOrder] = useState(null);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [isReservation, setIsReservation] = useState(false);
    const [isPending, setIsPending] = useState(false)
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [paymentProvider, setPaymentProvider] = useState("");
    const [isFormValid, setIsFormValid] = useState(false);
    const [haveTime, setHaveTime] = useState(false)
    const [localTravelData, setLocalTravelData] = useState(null);
    const [availablePaymentProviders, setAvailablePaymentProviders] = useState([]);

    const { fetchData } = useDataTravel();

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const data = await getPromoterService();
                setPromoter(data);

                let order;
                if (searchParams.get("payment_id")) {
                    order = await getOrderFromPendingPaymentId(searchParams.get("payment_id"));
                    setIsPending(true)
                } else {
                    order = await getOrder();
                    setIsReservation(order.booking.quotation.is_reservation);
                    setIsPending(false)
                }
                setOrder(order);
                setHaveTime(order.booking.quotation.has_available_time_spots);

                const _travelData = await fetchData(order.booking.quotation.public_id);
                setLocalTravelData(_travelData);

                if (!order.payment) {
                    navigate('/');
                    return;
                }

                // Fetch available payment providers
                try {
                    const providers = await getPaymentProviders();
                    setAvailablePaymentProviders(providers);
                } catch (e) {
                    setAvailablePaymentProviders([]);
                }

                setLoading(false); // Set loading to false only after all data is fetched
            } catch (e) {
                navigate("/");
            }
        };
        fetchAll();
    }, [navigate, searchParams, fetchData]);

    useEffect(() => {
        setIsSmallScreen(window.innerWidth <= 800);

        const handleResize = () => {
            setIsSmallScreen(window.innerWidth <= 800);
        };
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [isSmallScreen]);

    useEffect(() => {
        const formValid = paymentProvider && paymentProvider !== PAYMENT_PROVIDERS.MERCADO_PAGO && paymentProvider !== PAYMENT_PROVIDERS.REBILL;
        setIsFormValid(formValid);
    }, [paymentProvider]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isFormValid) {
            setLoading(true);
            try {
                await postCreateTransaction(order.payment.public_id, paymentProvider)
                navigate("/checkout/enjoy?external_reference=" + order.payment.public_id);
            } catch (e) {
                console.log(e);
                // TODO: Replace using TOAST or MODAL
                window.alert("There was a problem with the server!");
                setLoading(false);
            }
        }
    };

    // Helper function to check if a payment provider is available
    const isProviderAvailable = (code) => {
        return availablePaymentProviders.some(provider => provider.code === code);
    };

    // Show WhatsApp if no payment providers
    const noPaymentProviders = availablePaymentProviders.length === 0;

    // WhatsApp handler for no payment providers
    const handlePhoneClick = () => {
        if (!order?.booking?.public_id) return;
        const message = encodeURIComponent(
            `Hola, escribo porque necesito un link de pago para el booking ${order.booking.public_id}.`
        );
        window.open(`https://wa.me/5491127516586?text=${message}`, "_blank")?.focus();
    };

    // Determine if Rebill is the only available provider
    const isOnlyRebill =
        availablePaymentProviders.length === 1 &&
        (availablePaymentProviders[0].code === PAYMENT_PROVIDERS.REBILL || availablePaymentProviders[0].code === PAYMENT_PROVIDERS.REBILL_US);

    // Determine if Rebill is selected
    const isRebillSelected = paymentProvider === PAYMENT_PROVIDERS.REBILL || paymentProvider === PAYMENT_PROVIDERS.REBILL_US;

    if (loading) return <Loader/>;

    const currentUrl = window.location.protocol + "//" + window.location.host +  window.location.pathname + window.location.search

    return (
        <HelmetProvider>
            <MetaTags
                name={promoter.fullname}
                description={order?.booking?.quotation?.itinerary?.description}
                icon={promoter.logoURL}
                title={order?.booking?.quotation?.title}
                url={currentUrl}
                font={localTravelData.data.font ? localTravelData.data.font : null}
                primaryColor={localTravelData.data.colors.primary_color ? localTravelData.data.colors.primary_color : null}
                secondaryColor={localTravelData.data.colors.secondary_color ? localTravelData.data.colors.secondary_color : null}
            />
            <Header promoter={promoter}></Header>
            <div className="payments__container">
                <Breadcrumbs currentStep={currentStep} noGoBack={isPending}></Breadcrumbs>
                <div className="main-content">
                    <div className="payments-details__container">
                        {/* Only render payment methods that are available */}
                        {isProviderAvailable(PAYMENT_PROVIDERS.MERCADO_PAGO) && (
                            <MercadoPago order={order}></MercadoPago>
                        )}
                        {isProviderAvailable(PAYMENT_PROVIDERS.REBILL) && (
                            <Rebill
                                setPaymentProvider={setPaymentProvider} 
                                paymentProvider={paymentProvider}
                                order={order}
                                isOnlyProvider={isOnlyRebill}
                            />
                        )}
                        {isProviderAvailable(PAYMENT_PROVIDERS.REBILL_US) && (
                            <Rebill
                                setPaymentProvider={setPaymentProvider} 
                                paymentProvider={paymentProvider}
                                order={order}
                                isOnlyProvider={isOnlyRebill}
                                isUsProvider={true}
                            />
                        )}
                        {isProviderAvailable(PAYMENT_PROVIDERS.TRANSFER) && (
                            <Transferencia 
                                setPaymentProvider={setPaymentProvider} 
                                paymentProvider={paymentProvider}
                            ></Transferencia>
                        )}
                        {isProviderAvailable(PAYMENT_PROVIDERS.BANK) && (
                            <Bank 
                                setPaymentProvider={setPaymentProvider} 
                                paymentProvider={paymentProvider}
                            ></Bank>
                        )}
                        {/* Show WhatsApp link if no payment providers */}
                        {noPaymentProviders && (
                            <div className="no-payment-providers-whatsapp" style={{ margin: "24px 0" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <img src={whatsapp_icon} alt="WhatsApp" style={{ width: 24, height: 24 }} />
                                    <span>
                                        Lo sentimos, no contamos con medios de pago online para tu país.<br />
                                        Por favor,{" "}
                                        <span
                                            style={{ color: "#25D366", textDecoration: "underline", cursor: "pointer" }}
                                            onClick={handlePhoneClick}
                                        >
                                            contáctanos
                                        </span>
                                        {" "}para poder ofrecerte alternativas y concretar la compra.
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                    <OrderDetails
                        numPassengers={order.passengers.length}
                        quotation={order.booking.quotation}
                        passengers={order.passengers}
                        isReservation={isReservation}
                        isPending={isPending}
                        payment={order.payment ? order.payment : {}}
                        isSmallScreen={isSmallScreen}
                        nextStepLabel={isPending ? 'Pagar' : 'Reservar'}
                        isFormValid={isFormValid}
                        handleSubmitNextStep={handleSubmit}
                        selectDate={order.booking.selected_date ? order.booking.selected_date : ''}
                        haveTime={haveTime}
                        selectedOption={order.booking.selected_option ? order.booking.selected_option : ''}
                        isOnlyRebill={isOnlyRebill}
                        isRebillSelected={isRebillSelected}
                    />
                </div>
                {!isSmallScreen && !isRebillSelected && (
                    <div className="checkout-actions">
                        <button type="button" onClick={handleSubmit} disabled={!isFormValid}>
                            {isPending ? 'Pagar' : 'Reservar'}
                        </button>
                    </div>
                )}
            </div>
            <Footer promoter={promoter}></Footer>
        </HelmetProvider>
    );
};

export default Payments;
